import { SCAN_MODE } from './scanMode';
import type { ScanMode } from './scanMode';

/**
 * Whether this browser can reach a camera at all. False on a page served without HTTPS, in a
 * locked-down WebView, or on a machine with no camera support.
 */
export const hasCameraApi = (): boolean =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

/**
 * Whether a failure to start the camera means it cannot be used here — permission refused, no
 * camera, or a context that forbids it — rather than a passing problem (the camera briefly held by
 * another app, a scanner closed while its camera was still starting). Only the former should send
 * every screen back to list-first.
 */
export const isCameraUnavailableError = (error: unknown): boolean => {
  const { name = '', message = '' } = (error || {}) as {
    name?: string;
    message?: string;
  };
  return /NotAllowed|PermissionDenied|NotFound|DevicesNotFound|Security|NoCamera/i.test(
    `${name} ${message}`
  );
};

/**
 * Whether inputs that take a value by scanning OR typing should start on the scanner.
 *
 * Every device does — phone, handheld and desktop alike (a desktop's USB / Bluetooth scanner reads
 * into any open scanner as well as its webcam does). Laser mode always does: the trigger is the
 * scanner, however poor the camera. Camera mode does unless the camera is known to be blocked
 * (permission refused, no camera, no camera support): starting on a scanner that cannot see would
 * be a dead end, so the list comes first instead.
 */
export const isScanFirst = ({
  scanMode,
  cameraBlocked
}: {
  scanMode: ScanMode;
  cameraBlocked: boolean;
}): boolean => scanMode === SCAN_MODE.LASER || !cameraBlocked;
